import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useLeads, type NewLeadInput } from '../contexts/LeadsContext';
type AddLeadModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string) => void;
};
const emptyForm: NewLeadInput = {
  firstName: '',
  lastName: '',
  phone: '',
  email: ''
};
type Errors = Partial<Record<keyof NewLeadInput, string>>;
function validate(form: NewLeadInput): Errors {
  const errors: Errors = {};
  if (!form.firstName.trim()) errors.firstName = 'First name is required';
  if (!form.lastName.trim()) errors.lastName = 'Last name is required';
  const digits = form.phone.replace(/\D/g, '');
  if (!digits) errors.phone = 'Phone is required';else if (digits.length < 10) errors.phone = 'Enter a 10-digit phone number';
  if (!form.email.trim()) errors.email = 'Email is required';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email';
  return errors;
}
function Field({
  id,
  label,
  value,
  error,
  onChange,
  type = 'text',
  autoComplete,
  autoFocus
}: {
  id: keyof NewLeadInput;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  autoFocus?: boolean;
}) {
  return <div data-id="element-30" data-mp="wf4xf969cuyk">
      <label htmlFor={id} className="block text-[12px] font-medium text-ink" data-id="element-31" data-mp="wfwuypjf12rh">
        {label}
      </label>
      <input id={id} name={id} type={type} value={value} autoComplete={autoComplete} autoFocus={autoFocus} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} onChange={event => onChange(event.target.value)} className={['mt-1 w-full rounded-lg border px-3 py-2 text-[13px] text-ink placeholder:text-gray-400 focus:outline-none focus:ring-2', error ? 'border-danger focus:border-danger focus:ring-danger/20' : 'border-gray-200 focus:border-brand focus:ring-brand/20'].join(' ')} data-id="element-32" data-mp="3uzwm2kexiq9c" />
      {error && <p id={`${id}-error`} className="mt-1 text-[11.5px] text-danger" data-id="element-33" data-mp="3g8dpcnb9euvd">
          {error}
        </p>}
    </div>;
}
export function AddLeadModal({
  open,
  onClose,
  onCreated
}: AddLeadModalProps) {
  const {
    addLead
  } = useLeads();
  const [form, setForm] = useState<NewLeadInput>(emptyForm);
  const [errors, setErrors] = useState<Errors>({});
  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setErrors({});
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const update = (key: keyof NewLeadInput) => (value: string) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const found = validate(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    const lead = addLead(form);
    onCreated(lead.name);
    onClose();
  };
  return <AnimatePresence data-id="element-34" data-mp="1zomtsieazknq">
      {open && <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center" data-id="element-35" data-mp="1zpfw9768bo02">
          <motion.button type="button" aria-label="Close" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="absolute inset-0 h-full w-full cursor-default bg-ink/40" data-id="element-36" data-mp="2nfikfc70w9te" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="add-lead-title" initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 34
      }} className="relative w-full max-w-[440px] rounded-xl bg-white shadow-2xl" data-id="element-37" data-mp="120w9bj099ja4">
            <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4" data-id="element-38" data-mp="1dfpx1zr6040a">
              <div data-id="element-39" data-mp="18gmoi5bz7fnk">
                <h2 id="add-lead-title" className="text-[16px] font-bold text-ink" data-id="element-40" data-mp="266hmmedq5uen">
                  Add lead
                </h2>
                <p className="mt-0.5 text-[12.5px] text-gray-500" data-id="element-41" data-mp="21752w2i5h3ga">
                  Just enough to start dialing — the rest is captured on the call.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100" data-id="element-42" data-mp="1r9qn8541wip1">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-43" data-mp="fsijlm18b6lx" />
              </button>
            </header>

            <form onSubmit={submit} noValidate data-id="element-44" data-mp="2rj8erq73f1so">
              <div className="grid grid-cols-1 gap-3.5 px-5 py-4 sm:grid-cols-2" data-id="element-45" data-mp="2wfywsj2gk7gq">
                <Field id="firstName" label="First name" value={form.firstName} error={errors.firstName} onChange={update('firstName')} autoComplete="given-name" autoFocus data-id="element-46" data-mp="31fl64hf613k9" />
                <Field id="lastName" label="Last name" value={form.lastName} error={errors.lastName} onChange={update('lastName')} autoComplete="family-name" data-id="element-47" data-mp="3f6gja5wp66tn" />
                <Field id="phone" label="Phone" type="tel" value={form.phone} error={errors.phone} onChange={update('phone')} autoComplete="tel" data-id="element-48" data-mp="1psbfuvvs42xi" />
                <Field id="email" label="Email" type="email" value={form.email} error={errors.email} onChange={update('email')} autoComplete="email" data-id="element-49" data-mp="1iov6ag7u99hv" />
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3" data-id="element-50" data-mp="25p2ue99lsv9p">
                <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50" data-id="element-51" data-mp="21oivp19dtjlg">
                  Cancel
                </button>
                <button type="submit" className="rounded-lg bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-dark" data-id="element-52" data-mp="2ped5tb2bgu20">
                  Add lead
                </button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}